import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, isMfaPending, refreshSession, tokenStore } from './api';
import { useBranding } from './branding';
import { tenantSlugFromUrl } from './tenant';
import type { MeResponse, MfaPendingResponse, SessionResponse } from './types';

type Status = 'loading' | 'anonymous' | 'authenticated';

/** Why the last sign-out happened; the login page explains 'idle', 'expired' and 'tenant'. */
export type SignOutReason = 'user' | 'idle' | 'expired' | 'tenant';

interface AuthContextValue {
  status: Status;
  me: MeResponse | null;
  isOrgAdmin: boolean;
  /** Password sign-in: either a session, or an MFA ticket for the second step. */
  login: (email: string, password: string) => Promise<LoginResult>;
  /** Adopt a session returned by invite-accept / password-reset / code verification. */
  adoptSession: (session: SessionResponse) => Promise<MeResponse>;
  logout: (reason?: SignOutReason) => Promise<void>;
  refreshMe: () => Promise<MeResponse | null>;
  signedOutReason: SignOutReason | null;
}

export type LoginResult = { kind: 'session'; me: MeResponse } | { kind: 'mfa'; pending: MfaPendingResponse };

const AuthContext = createContext<AuthContextValue | null>(null);

/** Shared across tabs so activity anywhere keeps every tab signed in. */
export const ACTIVITY_KEY = 'crewline-last-activity';

/** Record activity now (used by the "stay signed in" button as well as the input listeners). */
export function touchActivity(): void {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [me, setMe] = useState<MeResponse | null>(null);
  const [signedOutReason, setSignedOutReason] = useState<SignOutReason | null>(null);
  const { setBranding } = useBranding();
  const queryClient = useQueryClient();
  const mounted = useRef(true);
  const booted = useRef(false);
  const statusRef = useRef<Status>('loading');
  statusRef.current = status;

  const loadMe = useCallback(async (): Promise<MeResponse | null> => {
    try {
      const data = await api.auth.me();
      if (!mounted.current) return data;
      setMe(data);
      setBranding(data.branding);
      setStatus('authenticated');
      return data;
    } catch {
      if (mounted.current) {
        setMe(null);
        setStatus('anonymous');
      }
      return null;
    }
  }, [setBranding]);

  // Restore the session on boot via the refresh cookie, exactly once. (StrictMode mounts twice in
  // development; two boot refreshes racing each other rotated the same token twice and signed people out.)
  useEffect(() => {
    mounted.current = true;
    if (!booted.current) {
      booted.current = true;
      void (async () => {
        const ok = await refreshSession();
        if (!mounted.current) return;
        const data = ok ? await loadMe() : null;
        if (!data) {
          setStatus('anonymous');
          return;
        }
        // /login/<slug> or ?tenant=<slug> names an organization: a session for a different one is dropped.
        const wanted = tenantSlugFromUrl();
        if (wanted && data.tenant.slug !== wanted) {
          try {
            await api.auth.logout();
          } catch {
            /* ignore */
          }
          tokenStore.set(null);
          if (!mounted.current) return;
          setMe(null);
          setStatus('anonymous');
          setSignedOutReason('tenant');
        }
      })();
    }
    return () => {
      mounted.current = false;
    };
  }, [loadMe]);

  // When the API client gives up on a 401, drop to anonymous.
  useEffect(() => {
    tokenStore.onUnauthorized(() => {
      if (statusRef.current === 'authenticated') setSignedOutReason('expired');
      setMe(null);
      setStatus('anonymous');
      queryClient.clear();
    });
    return () => tokenStore.onUnauthorized(null);
  }, [queryClient]);

  const adoptSession = useCallback(
    async (session: SessionResponse) => {
      tokenStore.set(session.accessToken);
      const data = await loadMe();
      if (!data) throw new Error('Could not load your account');
      setSignedOutReason(null);
      return data;
    },
    [loadMe],
  );

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const outcome = await api.auth.login(email, password);
      if (isMfaPending(outcome)) return { kind: 'mfa', pending: outcome };
      return { kind: 'session', me: await adoptSession(outcome) };
    },
    [adoptSession],
  );

  const logout = useCallback(
    async (reason: SignOutReason = 'user') => {
      try {
        await api.auth.logout();
      } catch {
        /* ignore */
      }
      tokenStore.set(null);
      setMe(null);
      setStatus('anonymous');
      setSignedOutReason(reason);
      queryClient.clear();
    },
    [queryClient],
  );

  // Inactivity sign-out (tenant setting security.idleTimeoutMinutes; 0 = off). Activity in any tab counts.
  const idleMinutes = me?.tenant.security?.idleTimeoutMinutes ?? 0;
  useEffect(() => {
    if (status !== 'authenticated' || idleMinutes <= 0) return;
    const limit = idleMinutes * 60_000;
    let last = Date.now();
    let lastWrite = 0;
    const write = (t: number) => {
      try {
        localStorage.setItem(ACTIVITY_KEY, String(t));
      } catch {
        /* ignore */
      }
    };
    const bump = () => {
      last = Date.now();
      if (last - lastWrite > 5_000) {
        lastWrite = last;
        write(last);
      }
    };
    const check = () => {
      let latest = last;
      try {
        const v = Number(localStorage.getItem(ACTIVITY_KEY));
        if (Number.isFinite(v) && v > latest) latest = v;
      } catch {
        /* ignore */
      }
      if (Date.now() - latest > limit) void logout('idle');
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    bump();
    write(last);
    for (const e of events) window.addEventListener(e, bump, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    const timer = window.setInterval(check, 30_000);
    return () => {
      for (const e of events) window.removeEventListener(e, bump);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(timer);
    };
  }, [status, idleMinutes, logout]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, me, isOrgAdmin: me?.user.tenantRole === 'org_admin', login, adoptSession, logout, refreshMe: loadMe, signedOutReason }),
    [status, me, login, adoptSession, logout, loadMe, signedOutReason],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** For components rendered only inside protected routes. */
export function useMe(): MeResponse {
  const { me } = useAuth();
  if (!me) throw new Error('useMe called outside an authenticated route');
  return me;
}
